<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p class="text-lg text-gray-600">Last updated: {{ lastUpdated }}</p>
      </div>

      <!-- Content -->
      <div class="bg-white shadow-lg rounded-lg p-8 prose prose-lg max-w-none">
        <!-- Introduction -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            1. Introduction
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Welcome to NeibrPay ("we," "our," or "us"). These Terms of Service
            ("Terms") govern your use of our HOA management platform and
            services (collectively, the "Service") operated by NeibrPay.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            By accessing or using our Service, you agree to be bound by these
            Terms. If you disagree with any part of these terms, then you may
            not access the Service.
          </p>
        </section>

        <!-- Acceptance of Terms -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            2. Acceptance of Terms
          </h2>
          <p class="text-gray-700 leading-relaxed">
            By creating an account, accessing, or using NeibrPay, you
            acknowledge that you have read, understood, and agree to be bound by
            these Terms and our Privacy Policy.
          </p>
        </section>

        <!-- Description of Service -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            3. Description of Service
          </h2>
          <p class="text-gray-700 leading-relaxed">
            NeibrPay is a comprehensive HOA (Homeowners Association) management
            platform that provides:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-4 space-y-2">
            <li>Financial management and accounting tools</li>
            <li>Communication and notification systems</li>
            <li>Property and resident management</li>
            <li>Document storage and management</li>
            <li>Payment processing and collection</li>
            <li>Maintenance request tracking</li>
            <li>Meeting and event management</li>
          </ul>
        </section>

        <!-- User Accounts -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            4. User Accounts
          </h2>
          <h3 class="text-xl font-medium text-gray-900 mb-3">
            4.1 Account Creation
          </h3>
          <p class="text-gray-700 leading-relaxed">
            To access certain features of the Service, you must create an
            account. You agree to provide accurate, current, and complete
            information during the registration process and to update such
            information to keep it accurate, current, and complete.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.2 Account Security
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You are responsible for safeguarding the password and for all
            activities that occur under your account. You agree to notify us
            immediately of any unauthorized use of your account.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.3 Account Types
          </h3>
          <p class="text-gray-700 leading-relaxed">
            NeibrPay supports different user roles including:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              <strong>Administrators:</strong> Full access to HOA management
              features
            </li>
            <li>
              <strong>Residents:</strong> Limited access to personal account and
              community information
            </li>
            <li>
              <strong>Bookkeepers:</strong> Access to financial and accounting
              features
            </li>
          </ul>
        </section>

        <!-- Acceptable Use -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            5. Acceptable Use
          </h2>
          <h3 class="text-xl font-medium text-gray-900 mb-3">
            5.1 Permitted Uses
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You may use the Service only for lawful purposes and in accordance
            with these Terms. You agree to use the Service only for legitimate
            HOA management activities.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            5.2 Prohibited Uses
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You agree not to use the Service:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              For any unlawful purpose or to solicit others to perform unlawful
              acts
            </li>
            <li>
              To violate any international, federal, provincial, or state
              regulations, rules, laws, or local ordinances
            </li>
            <li>
              To infringe upon or violate our intellectual property rights or
              the intellectual property rights of others
            </li>
            <li>
              To harass, abuse, insult, harm, defame, slander, disparage,
              intimidate, or discriminate
            </li>
            <li>To submit false or misleading information</li>
            <li>
              To upload or transmit viruses or any other type of malicious code
            </li>
            <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
            <li>For any obscene or immoral purpose</li>
            <li>
              To interfere with or circumvent the security features of the
              Service
            </li>
          </ul>
        </section>

        <!-- Privacy and Data Protection -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            6. Privacy and Data Protection
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Your privacy is important to us. Our Privacy Policy explains how we
            collect, use, and protect your information when you use our Service.
            By using our Service, you agree to the collection and use of
            information in accordance with our Privacy Policy.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            We implement appropriate technical and organizational measures to
            protect personal data against unauthorized access, alteration,
            disclosure, or destruction.
          </p>
        </section>

        <!-- Payment Terms -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            7. Payment Terms
          </h2>
          <h3 class="text-xl font-medium text-gray-900 mb-3">
            7.1 Subscription Fees
          </h3>
          <p class="text-gray-700 leading-relaxed">
            Access to certain features of the Service requires payment of
            subscription fees. All fees are non-refundable unless otherwise
            specified in these Terms.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.2 Billing
          </h3>
          <p class="text-gray-700 leading-relaxed">
            Subscription fees are billed in advance on a monthly or annual
            basis. You will be charged automatically unless you cancel your
            subscription before the next billing cycle.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.3 Trial Period
          </h3>
          <p class="text-gray-700 leading-relaxed">
            New users may be eligible for a 30-day free trial. The trial period
            begins when you create your account and ends 30 days later, at which
            point you will be charged the applicable subscription fee unless you
            cancel before the trial ends.
          </p>
        </section>

        <!-- Intellectual Property -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            8. Intellectual Property Rights
          </h2>
          <p class="text-gray-700 leading-relaxed">
            The Service and its original content, features, and functionality
            are and will remain the exclusive property of NeibrPay and its
            licensors. The Service is protected by copyright, trademark, and
            other laws.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            You retain ownership of any content you upload to the Service, but
            grant us a license to use, store, and process such content as
            necessary to provide the Service.
          </p>
        </section>

        <!-- Termination -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            9. Termination
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We may terminate or suspend your account and bar access to the
            Service immediately, without prior notice or liability, under our
            sole discretion, for any reason whatsoever, including without
            limitation if you breach the Terms.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            Upon termination, your right to use the Service will cease
            immediately. If you wish to terminate your account, you may simply
            discontinue using the Service or contact us to request account
            deletion.
          </p>
        </section>

        <!-- Disclaimers -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            10. Disclaimers
          </h2>
          <p class="text-gray-700 leading-relaxed">
            The information on this Service is provided on an "as is" basis. To
            the fullest extent permitted by law, this Company:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              Excludes all representations and warranties relating to this
              Service and its contents
            </li>
            <li>
              Excludes all liability for damages arising out of or in connection
              with your use of this Service
            </li>
          </ul>
        </section>

        <!-- Limitation of Liability -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            11. Limitation of Liability
          </h2>
          <p class="text-gray-700 leading-relaxed">
            In no event shall NeibrPay, nor its directors, employees, partners,
            agents, suppliers, or affiliates, be liable for any indirect,
            incidental, special, consequential, or punitive damages, including
            without limitation, loss of profits, data, use, goodwill, or other
            intangible losses, resulting from your use of the Service.
          </p>
        </section>

        <!-- Governing Law -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            12. Governing Law
          </h2>
          <p class="text-gray-700 leading-relaxed">
            These Terms shall be interpreted and governed by the laws of the
            United States, without regard to its conflict of law provisions. Our
            failure to enforce any right or provision of these Terms will not be
            considered a waiver of those rights.
          </p>
        </section>

        <!-- Changes to Terms -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            13. Changes to Terms
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right, at our sole discretion, to modify or replace
            these Terms at any time. If a revision is material, we will provide
            at least 30 days notice prior to any new terms taking effect.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            By continuing to access or use our Service after those revisions
            become effective, you agree to be bound by the revised terms.
          </p>
        </section>

        <!-- Contact Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            14. Contact Information
          </h2>
          <p class="text-gray-700 leading-relaxed">
            If you have any questions about these Terms of Service, please
            contact us:
          </p>
          <div class="mt-4 p-4 bg-gray-50 rounded-lg">
            <p class="text-gray-700">
              <strong>Email:</strong> legal@neibrpay.com
            </p>
            <p class="text-gray-700">
              <strong>Address:</strong> NeibrPay Legal Department
            </p>
            <p class="text-gray-700">123 Business Street, Suite 100</p>
            <p class="text-gray-700">City, State 12345</p>
            <p class="text-gray-700"><strong>Phone:</strong> (555) 123-4567</p>
          </div>
        </section>

        <!-- Footer -->
        <div class="border-t pt-8 mt-12">
          <p class="text-sm text-gray-500 text-center">
            These Terms of Service are effective as of {{ lastUpdated }} and
            will remain in effect except with respect to any changes in their
            provisions in the future, which will be in effect immediately after
            being posted on this page.
          </p>
        </div>
      </div>

      <!-- Back Button -->
      <div class="text-center mt-8">
        <button
          @click="goBack"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
        >
          <svg
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Dashboard
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';

const router = useRouter();

// Last updated date
const lastUpdated = 'September 30, 2025';

// Go back to previous page or dashboard
const goBack = () => {
  if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push('/');
  }
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose h2 {
  color: #111827;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.prose h3 {
  color: #111827;
}

.prose ul {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.prose li {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}
</style>
